import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  LayoutDashboard, Newspaper, Calendar, Image, Users, FolderKanban, 
  MessageSquare, Globe, Settings, LogOut, Plus, Trash2, Edit, 
  ChevronRight, Menu, X, FileText, Building2, PanelLeft, Shield, UserPlus, Check, Clock, Archive, Search, Bell, CheckCheck, Play, Video
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import ImageUpload from '../components/ImageUpload';
import VideoUpload from '../components/VideoUpload';
import PageEditor from '../components/PageEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/logo-gandiaye-md.webp";
const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Intercepteur global : rediriger vers login si token expiré
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401 && window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

const resolveUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('/api/uploads/') || url.startsWith('/api/files/')) return `${BASE_URL}${url}`;
  return url;
};

const getErrorMsg = (error, fallback = 'Erreur') => {
  const d = error?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  if (typeof d === 'object') return d.msg || d.message || fallback;
  return fallback;
};

const ROLE_LABELS = { super_admin: 'Super Admin', redacteur: 'Rédacteur', contributeur: 'Contributeur' };
const ROLE_COLORS = { super_admin: 'bg-red-100 text-red-800 border-red-200', redacteur: 'bg-blue-100 text-blue-800 border-blue-200', contributeur: 'bg-amber-100 text-amber-800 border-amber-200' };
const STATUS_LABELS = { brouillon: 'Brouillon', en_attente: 'En attente', publie: 'Publié', archive: 'Archivé' };
const STATUS_COLORS = { brouillon: 'bg-gray-100 text-gray-700', en_attente: 'bg-yellow-100 text-yellow-700', publie: 'bg-emerald-100 text-emerald-700', archive: 'bg-purple-100 text-purple-700' };

const ALL_MENU_ITEMS = [
  { id: 'dashboard', label: 'Tableau de bord', icon: LayoutDashboard, minRole: 'contributeur' },
  { id: 'pages', label: 'Pages', icon: PanelLeft, minRole: 'redacteur' },
  { id: 'news', label: 'Actualités', icon: Newspaper, minRole: 'contributeur' },
  { id: 'events', label: 'Événements', icon: Calendar, minRole: 'contributeur' },
  { id: 'gallery', label: 'Galerie', icon: Image, minRole: 'contributeur' },
  { id: 'council', label: 'Conseil Municipal', icon: Users, minRole: 'contributeur' },
  { id: 'projects', label: 'Projets', icon: FolderKanban, minRole: 'contributeur' },
  { id: 'services', label: 'Services', icon: Building2, minRole: 'contributeur' },
  { id: 'documents', label: 'Documents', icon: FileText, minRole: 'contributeur' },
  { id: 'videos', label: 'Médiathèque Vidéo', icon: Video, minRole: 'contributeur' },
  { id: 'messages', label: 'Messages', icon: MessageSquare, minRole: 'redacteur' },
  { id: 'diaspora', label: 'Diaspora', icon: Globe, minRole: 'contributeur' },
  { id: 'users', label: 'Utilisateurs', icon: Shield, minRole: 'super_admin' },
  { id: 'settings', label: 'Paramètres', icon: Settings, minRole: 'super_admin' },
];

const ROLE_HIERARCHY = { super_admin: 3, redacteur: 2, contributeur: 1 };
const hasAccess = (userRole, minRole) => (ROLE_HIERARCHY[userRole] || 0) >= (ROLE_HIERARCHY[minRole] || 0);
const canPublish = (role) => role === 'super_admin' || role === 'redacteur';

const StatusBadge = ({ status }) => {
  const s = status || 'publie';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[s] || STATUS_COLORS.brouillon}`}>{STATUS_LABELS[s] || s}</span>;
};

const RoleBadge = ({ role }) => (
  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${ROLE_COLORS[role] || ROLE_COLORS.contributeur}`} data-testid="user-role-badge">
    {ROLE_LABELS[role] || role}
  </span>
);

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [council, setCouncil] = useState([]);
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [diasporaProjects, setDiasporaProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [videos, setVideos] = useState([]);
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [adminSearchQuery, setAdminSearchQuery] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);

  const [siteSettings, setSiteSettings] = useState({
    mayor_message: '', mayor_name: 'Pape Songde Diop',
    mayor_photo: '',
    emergency_numbers: [{ title: 'Pompiers', number: '18' }, { title: 'Gendarmerie', number: '800 00 20 20' }, { title: 'Urgences Santé', number: '15' }],
    social_links: [{ title: 'Page Facebook officielle', url: '' }],
    contact_info: { address: '', phone: '', email: '', hours: '' },
    key_stats: { population: '15 000+', schools: '12', surface: '150 km²', creation: '1996' },
  });

  const role = user?.role || 'contributeur';
  const menuItems = useMemo(() => ALL_MENU_ITEMS.filter(item => hasAccess(role, item.minRole)), [role]);

  const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      
      const requests = [
        axios.get(`${API}/admin/stats`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/news`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/events`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/gallery`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/council`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/projects`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/services`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/documents`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/contact`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/diaspora/projects`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/users`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/admin/videos`, { headers }).catch(() => ({ data: [] })),
      ];
      
      const results = await Promise.all(requests);
      setStats(results[0].data);
      setNews(results[1].data);
      setEvents(results[2].data);
      setGallery(results[3].data);
      setCouncil(results[4].data);
      setProjects(results[5].data);
      setServices(results[6].data);
      setDocuments(results[7].data);
      setMessages(results[8].data);
      setDiasporaProjects(results[9].data);
      setUsers(results[10].data);
      setVideos(results[11].data);
      // Fetch settings
      try {
        const settingsRes = await axios.get(`${API}/settings`);
        const s = settingsRes.data;
        setSiteSettings({
          mayor_message: s.mayor_message || '',
          mayor_name: s.mayor_name || 'Pape Songde Diop',
          mayor_photo: s.mayor_photo || '',
          emergency_numbers: Array.isArray(s.emergency_numbers) ? s.emergency_numbers : [{ title: 'Pompiers', number: '18' }],
          social_links: Array.isArray(s.social_links) ? s.social_links : [{ title: 'Page Facebook', url: '' }],
          contact_info: s.contact_info || { address: '', phone: '', email: '', hours: '' },
          key_stats: s.key_stats || { population: '15 000+', schools: '12', surface: '150 km²', creation: '1996' },
        });
      } catch {}
      // Fetch notifications
      try {
        const notifRes = await axios.get(`${API}/admin/notifications`, { headers });
        setNotifications(notifRes.data.notifications || []);
        setUnreadCount(notifRes.data.unread_count || 0);
      } catch {}
    } catch (error) {
      if (error.response?.status === 401) handleLogout();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    if (!token || !userData) { navigate('/admin/login'); return; }
    // Refresh user data from API to get latest role
    const headers = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/auth/me`, { headers }).then(res => {
      const freshUser = res.data;
      localStorage.setItem('user', JSON.stringify(freshUser));
      setUser(freshUser);
    }).catch((err) => {
      if (err.response?.status === 401) { handleLogout(); return; }
      setUser(JSON.parse(userData));
    });
    fetchData();
  }, [navigate, fetchData]);

  const handleLogout = () => { localStorage.removeItem('token'); localStorage.removeItem('user'); navigate('/admin/login'); };

  const openModal = (type, item = null) => { setModalType(type); setEditingItem(item); setFormData(item ? { ...item } : {}); setIsModalOpen(true); };
  const closeModal = () => { setIsModalOpen(false); setEditingItem(null); setFormData({}); };

  const canEditItem = (item) => {
    if (role === 'super_admin' || role === 'redacteur') return true;
    return item.author_id === user?.id;
  };

  const canDeleteItem = (item) => {
    if (role === 'super_admin') return true;
    if (role === 'redacteur') return true;
    if (role === 'contributeur') {
      return item.author_id === user?.id && (item.status || item.content_status) !== 'publie';
    }
    return false;
  };

  const handleStatusChange = async (contentType, itemId, newStatus) => {
    try {
      await axios.put(`${API}/admin/status/${contentType}/${itemId}?new_status=${newStatus}`, {}, { headers: getAuthHeaders() });
      toast.success(`Statut changé en "${STATUS_LABELS[newStatus]}"`);
      fetchData();
    } catch (error) {
      toast.error(getErrorMsg(error, 'Erreur lors du changement de statut'));
    }
  };

  const handleSave = async () => {
    const headers = getAuthHeaders();
    try {
      let endpoint = '';
      let method = editingItem ? 'put' : 'post';
      let dataToSend = { ...formData };
      // Map publication control to status field
      if (dataToSend._pub_status) {
        dataToSend.status = dataToSend._pub_status;
        dataToSend.is_published = dataToSend._pub_status === 'publie';
        delete dataToSend._pub_status;
      }

      switch (modalType) {
        case 'news':
          dataToSend.category = dataToSend.category || 'general';
          dataToSend.is_published = dataToSend.is_published !== false;
          break;
        case 'events':
          dataToSend.is_published = dataToSend.is_published !== false;
          break;
        case 'gallery':
          dataToSend.category = dataToSend.category || 'general';
          break;
        case 'projects':
          dataToSend.category = dataToSend.category || 'infrastructure';
          dataToSend.status = dataToSend.status || 'en_cours';
          dataToSend.progress = dataToSend.progress || 0;
          break;
        case 'services':
          dataToSend.category = dataToSend.category || 'administration';
          break;
        case 'documents':
          dataToSend.category = dataToSend.category || 'etat_civil';
          break;
        case 'videos':
          dataToSend.category = dataToSend.category || 'general';
          dataToSend.is_published = dataToSend.is_published !== false;
          break;
        case 'diaspora/projects':
          dataToSend.category = dataToSend.category || 'education';
          break;
        case 'users':
          if (editingItem) {
            await axios.put(`${API}/admin/users/${editingItem.id}`, { full_name: dataToSend.full_name, role: dataToSend.role }, { headers });
          } else {
            await axios.post(`${API}/admin/users`, { email: dataToSend.email, password: dataToSend.password, full_name: dataToSend.full_name, role: dataToSend.role || 'contributeur' }, { headers });
          }
          toast.success(editingItem ? 'Utilisateur modifié !' : 'Utilisateur créé !');
          closeModal();
          fetchData();
          return;
        default: break;
      }

      switch (modalType) {
        case 'news': endpoint = editingItem ? `${API}/admin/news/${editingItem.id}` : `${API}/admin/news`; break;
        case 'events': endpoint = editingItem ? `${API}/admin/events/${editingItem.id}` : `${API}/admin/events`; break;
        case 'gallery': endpoint = editingItem ? `${API}/admin/gallery/${editingItem.id}` : `${API}/admin/gallery`; break;
        case 'council': endpoint = editingItem ? `${API}/admin/council/${editingItem.id}` : `${API}/admin/council`; break;
        case 'projects': endpoint = editingItem ? `${API}/admin/projects/${editingItem.id}` : `${API}/admin/projects`; break;
        case 'services': endpoint = editingItem ? `${API}/admin/services/${editingItem.id}` : `${API}/admin/services`; break;
        case 'documents': endpoint = editingItem ? `${API}/admin/documents/${editingItem.id}` : `${API}/admin/documents`; break;
        case 'videos': endpoint = editingItem ? `${API}/admin/videos/${editingItem.id}` : `${API}/admin/videos`; break;
        case 'diaspora/projects':
          if (editingItem) {
            await axios.put(`${API}/admin/diaspora/projects/${editingItem.id}/status?status=${dataToSend.status}`, {}, { headers });
            toast.success('Statut mis à jour !');
            closeModal(); fetchData(); return;
          }
          endpoint = `${API}/diaspora/projects`;
          break;
        default: return;
      }

      if (method === 'put') await axios.put(endpoint, dataToSend, { headers });
      else await axios.post(endpoint, dataToSend, { headers });

      toast.success('Enregistré avec succès !');
      closeModal();
      fetchData();
    } catch (error) {
      toast.error(getErrorMsg(error, "Erreur lors de l'enregistrement"));
    }
  };

  const handleDelete = async (type, id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet élément ?')) return;
    try {
      const endpoint = type === 'diaspora/projects' ? `${API}/admin/diaspora/projects/${id}` : `${API}/admin/${type}/${id}`;
      await axios.delete(endpoint, { headers: getAuthHeaders() });
      toast.success('Supprimé avec succès !');
      fetchData();
    } catch (error) {
      toast.error(getErrorMsg(error, 'Erreur lors de la suppression'));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: getAuthHeaders() });
      toast.success('Utilisateur supprimé');
      fetchData();
    } catch (error) {
      toast.error(getErrorMsg(error, 'Erreur'));
    }
  };

  const markNotifRead = async (notifId) => {
    await axios.put(`${API}/admin/notifications/${notifId}/read`, {}, { headers: getAuthHeaders() });
    setNotifications(prev => prev.map(n => n.id === notifId ? { ...n, is_read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllNotifsRead = async () => {
    await axios.put(`${API}/admin/notifications/read-all`, {}, { headers: getAuthHeaders() });
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  // ==================== RENDERERS ====================

  const renderDashboard = () => (
    <div className="space-y-8" data-testid="admin-dashboard-content">
      <h2 className="text-2xl font-heading font-bold">Tableau de bord</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="p-6 text-center"><Newspaper className="w-8 h-8 text-emerald-600 mx-auto mb-2" /><div className="text-3xl font-bold">{stats.news || 0}</div><div className="text-sm text-neutral-500">Actualités</div></CardContent></Card>
        <Card><CardContent className="p-6 text-center"><Calendar className="w-8 h-8 text-blue-500 mx-auto mb-2" /><div className="text-3xl font-bold">{stats.events || 0}</div><div className="text-sm text-neutral-500">Événements</div></CardContent></Card>
        <Card><CardContent className="p-6 text-center"><MessageSquare className="w-8 h-8 text-yellow-500 mx-auto mb-2" /><div className="text-3xl font-bold">{stats.unread_messages || 0}</div><div className="text-sm text-neutral-500">Messages non lus</div></CardContent></Card>
        <Card><CardContent className="p-6 text-center"><Globe className="w-8 h-8 text-purple-500 mx-auto mb-2" /><div className="text-3xl font-bold">{stats.diaspora_projects || 0}</div><div className="text-sm text-neutral-500">Projets Diaspora</div></CardContent></Card>
        <Card><CardContent className="p-6 text-center"><Video className="w-8 h-8 text-red-500 mx-auto mb-2" /><div className="text-3xl font-bold">{stats.videos || 0}</div><div className="text-sm text-neutral-500">Vidéos</div></CardContent></Card>
      </div>
      {hasAccess(role, 'redacteur') && (
        <Card>
          <CardHeader><CardTitle className="flex items-center justify-between"><span>Messages récents</span><Button variant="ghost" size="sm" onClick={() => setActiveTab('messages')}>Voir tout <ChevronRight size={16} /></Button></CardTitle></CardHeader>
          <CardContent>
            {messages.slice(0, 5).map((msg) => (
              <div key={msg.id} className={`p-4 border-b last:border-0 ${!msg.is_read ? 'bg-emerald-50' : ''}`}>
                <div className="flex justify-between items-start">
                  <div><p className="font-medium">{msg.name}</p><p className="text-sm text-neutral-500">{msg.subject}</p></div>
                  <span className="text-xs text-neutral-400">{new Date(msg.created_at).toLocaleDateString('fr-FR')}</span>
                </div>
              </div>
            ))}
            {messages.length === 0 && <p className="text-center text-neutral-500 py-4">Aucun message</p>}
          </CardContent>
        </Card>
      )}
    </div>
  );

  const renderList = (items, type, columns) => {
    const contentType = type === 'diaspora/projects' ? null : type;
    const showStatusCol = ['news', 'events', 'gallery', 'documents', 'videos'].includes(type);

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-heading font-bold capitalize" data-testid={`${type}-section-title`}>{
            type === 'news' ? 'Actualités' : type === 'events' ? 'Événements' : type === 'gallery' ? 'Galerie' :
            type === 'council' ? 'Conseil Municipal' : type === 'projects' ? 'Projets' : type === 'services' ? 'Services' :
            type === 'documents' ? 'Documents' : type === 'diaspora/projects' ? 'Projets Diaspora' : type
          }</h2>
          <Button onClick={() => openModal(type)} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`add-${type}-btn`}>
            <Plus size={18} className="mr-2" /> Ajouter
          </Button>
        </div>
        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="w-full">
            <thead className="bg-neutral-100">
              <tr>
                {columns.map((col) => <th key={col.key} className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">{col.label}</th>)}
                {showStatusCol && <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Statut</th>}
                <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-t hover:bg-neutral-50">
                  {columns.map((col) => <td key={col.key} className="px-4 py-3 text-sm">{col.render ? col.render(item[col.key], item) : item[col.key]}</td>)}
                  {showStatusCol && (
                    <td className="px-4 py-3 text-sm">
                      {canPublish(role) && contentType ? (
                        <Select value={item.status || 'publie'} onValueChange={(val) => handleStatusChange(contentType, item.id, val)}>
                          <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="brouillon">Brouillon</SelectItem>
                            <SelectItem value="en_attente">En attente</SelectItem>
                            <SelectItem value="publie">Publié</SelectItem>
                            <SelectItem value="archive">Archivé</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <StatusBadge status={item.status} />
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3 text-right">
                    <div className="flex justify-end gap-1">
                      {canEditItem(item) && <Button variant="ghost" size="sm" onClick={() => openModal(type, item)} data-testid={`edit-${type}-${item.id}`}><Edit size={16} /></Button>}
                      {canDeleteItem(item) && <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(type, item.id)} data-testid={`delete-${type}-${item.id}`}><Trash2 size={16} /></Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {items.length === 0 && <p className="text-center text-neutral-500 py-8">Aucun élément</p>}
        </div>
      </div>
    );
  };

  const renderMessages = () => (
    <div className="space-y-4">
      <h2 className="text-2xl font-heading font-bold">Messages</h2>
      <div className="space-y-4">
        {messages.map((msg) => (
          <Card key={msg.id} className={!msg.is_read ? 'border-l-4 border-emerald-500' : ''}>
            <CardContent className="p-6">
              <div className="flex justify-between items-start mb-4">
                <div><p className="font-semibold text-lg">{msg.subject}</p><p className="text-sm text-neutral-500">De: {msg.name} ({msg.email}) - {msg.department}</p></div>
                <span className="text-xs text-neutral-400">{new Date(msg.created_at).toLocaleString('fr-FR')}</span>
              </div>
              <p className="text-neutral-700 mb-4">{msg.message}</p>
              <div className="flex gap-2">
                {!msg.is_read && <Button variant="outline" size="sm" onClick={async () => { await axios.put(`${API}/admin/contact/${msg.id}/read`, {}, { headers: getAuthHeaders() }); fetchData(); }}>Marquer comme lu</Button>}
                <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete('contact', msg.id)}>Supprimer</Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {messages.length === 0 && <Card><CardContent className="p-8 text-center text-neutral-500">Aucun message</CardContent></Card>}
      </div>
    </div>
  );

  const renderUsers = () => (
    <div className="space-y-4" data-testid="users-management-section">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-heading font-bold">Gestion des Utilisateurs</h2>
        <Button onClick={() => openModal('users')} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-user-btn">
          <UserPlus size={18} className="mr-2" /> Nouvel utilisateur
        </Button>
      </div>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-neutral-100">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Nom</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Email</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Rôle</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-neutral-700">Inscrit le</th>
              <th className="px-4 py-3 text-right text-sm font-semibold text-neutral-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t hover:bg-neutral-50">
                <td className="px-4 py-3 text-sm font-medium">{u.full_name}</td>
                <td className="px-4 py-3 text-sm text-neutral-600">{u.email}</td>
                <td className="px-4 py-3 text-sm"><RoleBadge role={u.role} /></td>
                <td className="px-4 py-3 text-sm text-neutral-500">{new Date(u.created_at).toLocaleDateString('fr-FR')}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openModal('users', u)} data-testid={`edit-user-${u.id}`}><Edit size={16} /></Button>
                    {u.id !== user?.id && <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDeleteUser(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 size={16} /></Button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {users.length === 0 && <p className="text-center text-neutral-500 py-8">Aucun utilisateur</p>}
      </div>
    </div>
  );

  const updateListItem = (listKey, index, field, value) => {
    const list = [...(siteSettings[listKey] || [])];
    list[index] = { ...list[index], [field]: value };
    setSiteSettings({ ...siteSettings, [listKey]: list });
  };
  const addListItem = (listKey, template) => {
    setSiteSettings({ ...siteSettings, [listKey]: [...(siteSettings[listKey] || []), template] });
  };
  const removeListItem = (listKey, index) => {
    setSiteSettings({ ...siteSettings, [listKey]: siteSettings[listKey].filter((_, i) => i !== index) });
  };

  const renderSettings = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-heading font-bold">Paramètres du Site</h2>
      <div className="grid lg:grid-cols-2 gap-6">
        <Card><CardHeader><CardTitle>Mot du Maire</CardTitle></CardHeader><CardContent className="space-y-4">
          <div><label className="block text-sm font-medium mb-2">Nom du Maire</label><Input value={siteSettings.mayor_name} onChange={(e) => setSiteSettings({...siteSettings, mayor_name: e.target.value})} data-testid="settings-mayor-name" /></div>
          <div><label className="block text-sm font-medium mb-2">URL Photo du Maire</label><Input value={siteSettings.mayor_photo} onChange={(e) => setSiteSettings({...siteSettings, mayor_photo: e.target.value})} /></div>
          <div><label className="block text-sm font-medium mb-2">Message du Maire</label><Textarea value={siteSettings.mayor_message} onChange={(e) => setSiteSettings({...siteSettings, mayor_message: e.target.value})} rows={6} /></div>
        </CardContent></Card>
        <Card><CardHeader><CardTitle>Coordonnées</CardTitle></CardHeader><CardContent className="space-y-4">
          <div><label className="block text-sm font-medium mb-2">Adresse</label><Input value={siteSettings.contact_info?.address || ''} onChange={(e) => setSiteSettings({...siteSettings, contact_info: {...siteSettings.contact_info, address: e.target.value}})} data-testid="settings-address" /></div>
          <div><label className="block text-sm font-medium mb-2">Téléphone</label><Input value={siteSettings.contact_info?.phone || ''} onChange={(e) => setSiteSettings({...siteSettings, contact_info: {...siteSettings.contact_info, phone: e.target.value}})} data-testid="settings-phone" /></div>
          <div><label className="block text-sm font-medium mb-2">Email</label><Input value={siteSettings.contact_info?.email || ''} onChange={(e) => setSiteSettings({...siteSettings, contact_info: {...siteSettings.contact_info, email: e.target.value}})} data-testid="settings-email" /></div>
          <div><label className="block text-sm font-medium mb-2">Horaires</label><Input value={siteSettings.contact_info?.hours || ''} onChange={(e) => setSiteSettings({...siteSettings, contact_info: {...siteSettings.contact_info, hours: e.target.value}})} data-testid="settings-hours" /></div>
        </CardContent></Card>
        <Card><CardHeader><CardTitle className="flex items-center justify-between">Numéros d'Urgence <Button size="sm" variant="outline" onClick={() => addListItem('emergency_numbers', { title: '', number: '' })} data-testid="add-emergency"><Plus size={14} className="mr-1" />Ajouter</Button></CardTitle></CardHeader><CardContent className="space-y-3">
          {(siteSettings.emergency_numbers || []).map((item, i) => (
            <div key={i} className="flex items-end gap-2 p-3 bg-neutral-50 rounded-lg" data-testid={`emergency-${i}`}>
              <div className="flex-1"><label className="block text-xs text-neutral-500 mb-1">Titre</label><Input value={item.title} onChange={(e) => updateListItem('emergency_numbers', i, 'title', e.target.value)} placeholder="Ex: Pompiers" /></div>
              <div className="flex-1"><label className="block text-xs text-neutral-500 mb-1">Numéro</label><Input value={item.number} onChange={(e) => updateListItem('emergency_numbers', i, 'number', e.target.value)} placeholder="Ex: 18" /></div>
              <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0" onClick={() => removeListItem('emergency_numbers', i)} data-testid={`remove-emergency-${i}`}><Trash2 size={16} /></Button>
            </div>
          ))}
          {(siteSettings.emergency_numbers || []).length === 0 && <p className="text-sm text-neutral-400 text-center py-4">Aucun numéro. Cliquez "Ajouter".</p>}
        </CardContent></Card>
        <Card><CardHeader><CardTitle className="flex items-center justify-between">Réseaux Sociaux <Button size="sm" variant="outline" onClick={() => addListItem('social_links', { title: '', url: '' })} data-testid="add-social"><Plus size={14} className="mr-1" />Ajouter</Button></CardTitle></CardHeader><CardContent className="space-y-3">
          {(siteSettings.social_links || []).map((item, i) => (
            <div key={i} className="flex items-end gap-2 p-3 bg-neutral-50 rounded-lg" data-testid={`social-${i}`}>
              <div className="flex-1"><label className="block text-xs text-neutral-500 mb-1">Titre</label><Input value={item.title} onChange={(e) => updateListItem('social_links', i, 'title', e.target.value)} placeholder="Ex: Page Facebook officielle" /></div>
              <div className="flex-1"><label className="block text-xs text-neutral-500 mb-1">URL</label><Input value={item.url} onChange={(e) => updateListItem('social_links', i, 'url', e.target.value)} placeholder="https://facebook.com/..." /></div>
              <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 hover:bg-red-50 flex-shrink-0" onClick={() => removeListItem('social_links', i)} data-testid={`remove-social-${i}`}><Trash2 size={16} /></Button>
            </div>
          ))}
          {(siteSettings.social_links || []).length === 0 && <p className="text-sm text-neutral-400 text-center py-4">Aucun lien. Cliquez "Ajouter".</p>}
        </CardContent></Card>
      </div>
      <div className="flex justify-end">
        <Button onClick={async () => { try { await axios.put(`${API}/admin/settings`, siteSettings, { headers: getAuthHeaders() }); toast.success('Paramètres enregistrés !'); } catch { toast.error("Erreur lors de l'enregistrement"); } }} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-settings-btn">Enregistrer les paramètres</Button>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return renderDashboard();
      case 'pages': return <PageEditor getAuthHeaders={getAuthHeaders} />;
      case 'news':
        return renderList(news, 'news', [
          { key: 'title', label: 'Titre', render: (v, item) => <span className="flex items-center gap-1.5">{v}{item.video_url ? <Play size={14} className="text-emerald-600 shrink-0" /> : null}</span> },
          { key: 'category', label: 'Catégorie' },
          { key: 'image_url', label: 'Image', render: (v) => v ? <img src={resolveUrl(v)} alt="" loading="lazy" className="w-10 h-10 object-cover rounded" /> : '-' },
          { key: 'created_at', label: 'Date', render: (v) => new Date(v).toLocaleDateString('fr-FR') },
        ]);
      case 'events':
        return renderList(events, 'events', [
          { key: 'title', label: 'Titre' },
          { key: 'event_date', label: 'Date' },
          { key: 'location', label: 'Lieu' },
        ]);
      case 'gallery':
        return renderList(gallery, 'gallery', [
          { key: 'title', label: 'Titre' },
          { key: 'category', label: 'Catégorie' },
          { key: 'image_url', label: 'Image', render: (v) => v ? <img src={resolveUrl(v)} alt="" loading="lazy" className="w-12 h-12 object-cover rounded" /> : '-' },
        ]);
      case 'council':
        return renderList(council, 'council', [
          { key: 'full_name', label: 'Nom' },
          { key: 'position', label: 'Fonction' },
          { key: 'commission', label: 'Commission' },
        ]);
      case 'projects':
        return renderList(projects, 'projects', [
          { key: 'title', label: 'Titre' },
          { key: 'category', label: 'Catégorie' },
          { key: 'status', label: 'Avancement', render: (_, item) => `${item.progress || 0}%` },
        ]);
      case 'services':
        return renderList(services, 'services', [
          { key: 'name', label: 'Nom' },
          { key: 'category', label: 'Catégorie' },
          { key: 'hours', label: 'Horaires' },
        ]);
      case 'documents':
        return renderList(documents, 'documents', [
          { key: 'title', label: 'Titre' },
          { key: 'category', label: 'Catégorie' },
          { key: 'file_url', label: 'Fichier', render: (v) => v ? <a href={resolveUrl(v)} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">Ouvrir</a> : '-' },
        ]);
      case 'messages': return renderMessages();
      case 'diaspora':
        return renderList(diasporaProjects, 'diaspora/projects', [
          { key: 'title', label: 'Titre' },
          { key: 'proposer_name', label: 'Proposé par' },
          { key: 'category', label: 'Catégorie' },
          { key: 'status', label: 'Statut' },
        ]);
      case 'users': return renderUsers();
      case 'settings': return renderSettings();
      case 'videos':
        return renderList(videos, 'videos', [
          { key: 'title', label: 'Titre', render: (v) => <span className="flex items-center gap-1.5"><Play size={14} className="text-emerald-600 shrink-0" />{v}</span> },
          { key: 'category', label: 'Catégorie' },
          { key: 'video_type', label: 'Source', render: (v) => v === 'youtube' ? 'YouTube' : v === 'vimeo' ? 'Vimeo' : 'Upload' },
          { key: 'created_at', label: 'Date', render: (v) => new Date(v).toLocaleDateString('fr-FR') },
        ]);
      default: return renderDashboard();
    }
  };

  const renderPublicationControl = () => {
    if (role === 'contributeur') {
      return (
        <div className="border rounded-lg p-3 bg-amber-50">
          <label className="block text-sm font-medium mb-2 text-amber-800">Statut de publication</label>
          <Select value={formData._pub_status || 'brouillon'} onValueChange={(v) => setFormData({ ...formData, _pub_status: v, is_published: false })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="brouillon"><span className="flex items-center gap-2"><Edit size={14} /> Brouillon</span></SelectItem>
              <SelectItem value="en_attente"><span className="flex items-center gap-2"><Clock size={14} /> Soumettre pour relecture</span></SelectItem>
            </SelectContent>
          </Select>
        </div>
      );
    }
    if (canPublish(role)) {
      return (
        <div className="border rounded-lg p-3 bg-blue-50">
          <label className="block text-sm font-medium mb-2 text-blue-800">Statut de publication</label>
          <Select value={formData._pub_status || (formData.is_published !== false ? 'publie' : 'brouillon')} onValueChange={(v) => setFormData({ ...formData, _pub_status: v, is_published: v === 'publie' })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="brouillon"><span className="flex items-center gap-2"><Edit size={14} /> Brouillon</span></SelectItem>
              <SelectItem value="en_attente"><span className="flex items-center gap-2"><Clock size={14} /> En attente</span></SelectItem>
              <SelectItem value="publie"><span className="flex items-center gap-2"><Check size={14} /> Publier</span></SelectItem>
              <SelectItem value="archive"><span className="flex items-center gap-2"><Archive size={14} /> Archiver</span></SelectItem>
            </SelectContent>
          </Select>
        </div>
      );
    }
    return null;
  };

  const renderModalContent = () => {
    switch (modalType) {
      case 'news':
        return (<div className="space-y-4">
          <Input placeholder="Titre" value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} data-testid="modal-news-title" />
          <Input placeholder="Résumé" value={formData.summary || ''} onChange={(e) => setFormData({ ...formData, summary: e.target.value })} />
          <Textarea placeholder="Contenu" rows={5} value={formData.content || ''} onChange={(e) => setFormData({ ...formData, content: e.target.value })} />
          <ImageUpload value={formData.image_url || ''} onChange={(url) => setFormData({ ...formData, image_url: url })} placeholder="URL de l'image ou télécharger" />
          <div>
            <label className="text-sm font-medium text-neutral-700 mb-1 block">Vidéo (optionnel)</label>
            <VideoUpload value={formData.video_url || ''} videoType={formData.video_type || null} onChange={(url, type) => setFormData({ ...formData, video_url: url || null, video_type: type || null })} />
          </div>
          <Select value={formData.category || 'general'} onValueChange={(value) => setFormData({ ...formData, category: value })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="general">Général</SelectItem><SelectItem value="evenement">Événement</SelectItem><SelectItem value="annonce">Annonce</SelectItem><SelectItem value="projet">Projet</SelectItem>
            </SelectContent>
          </Select>
          {renderPublicationControl()}
        </div>);
      case 'events':
        return (<div className="space-y-4">
          <Input placeholder="Titre" value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
          <Textarea placeholder="Description" rows={3} value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          <Input type="date" placeholder="Date" value={formData.event_date || ''} onChange={(e) => setFormData({ ...formData, event_date: e.target.value })} />
          <Input placeholder="Heure (ex: 10h00)" value={formData.event_time || ''} onChange={(e) => setFormData({ ...formData, event_time: e.target.value })} />
          <Input placeholder="Lieu" value={formData.location || ''} onChange={(e) => setFormData({ ...formData, location: e.target.value })} />
          <ImageUpload value={formData.image_url || ''} onChange={(url) => setFormData({ ...formData, image_url: url })} placeholder="Image de l'événement (optionnel)" />
          {renderPublicationControl()}
        </div>);
      case 'gallery':
        return (<div className="space-y-4">
          <Input placeholder="Titre" value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
          <ImageUpload value={formData.image_url || ''} onChange={(url) => setFormData({ ...formData, image_url: url })} placeholder="URL de l'image ou télécharger" />
          <Textarea placeholder="Description (optionnel)" rows={2} value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          <Select value={formData.category || 'general'} onValueChange={(value) => setFormData({ ...formData, category: value })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="general">Général</SelectItem><SelectItem value="evenement">Événement</SelectItem><SelectItem value="patrimoine">Patrimoine</SelectItem><SelectItem value="culture">Culture</SelectItem>
            </SelectContent>
          </Select>
          {renderPublicationControl()}
        </div>);
      case 'council':
        return (<div className="space-y-4">
          <Input placeholder="Nom complet" value={formData.full_name || ''} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} />
          <Input placeholder="Fonction" value={formData.position || ''} onChange={(e) => setFormData({ ...formData, position: e.target.value })} />
          <Input placeholder="Commission" value={formData.commission || ''} onChange={(e) => setFormData({ ...formData, commission: e.target.value })} />
          <ImageUpload value={formData.photo_url || ''} onChange={(url) => setFormData({ ...formData, photo_url: url })} placeholder="Photo du membre" />
          <Input type="number" placeholder="Ordre d'affichage" value={formData.order || 0} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })} />
        </div>);
      case 'projects':
        return (<div className="space-y-4">
          <Input placeholder="Titre" value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
          <Textarea placeholder="Description" rows={3} value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          <Select value={formData.category || 'infrastructure'} onValueChange={(value) => setFormData({ ...formData, category: value })}>
            <SelectTrigger><SelectValue placeholder="Catégorie" /></SelectTrigger>
            <SelectContent><SelectItem value="infrastructure">Infrastructure</SelectItem><SelectItem value="environment">Environnement</SelectItem><SelectItem value="partnership">Partenariat</SelectItem></SelectContent>
          </Select>
          <Select value={formData.status || 'en_cours'} onValueChange={(value) => setFormData({ ...formData, status: value })}>
            <SelectTrigger><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent><SelectItem value="planifie">Planifié</SelectItem><SelectItem value="en_cours">En cours</SelectItem><SelectItem value="termine">Terminé</SelectItem></SelectContent>
          </Select>
          <Input type="number" placeholder="Avancement (%)" value={formData.progress || 0} onChange={(e) => setFormData({ ...formData, progress: parseInt(e.target.value) })} />
          <Input placeholder="Budget" value={formData.budget || ''} onChange={(e) => setFormData({ ...formData, budget: e.target.value })} />
          <ImageUpload value={formData.image_url || ''} onChange={(url) => setFormData({ ...formData, image_url: url })} placeholder="Image du projet (optionnel)" />
        </div>);
      case 'services':
        return (<div className="space-y-4">
          <Input placeholder="Nom du service" value={formData.name || ''} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
          <Textarea placeholder="Description" rows={3} value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          <Select value={formData.category || 'administration'} onValueChange={(value) => setFormData({ ...formData, category: value })}>
            <SelectTrigger><SelectValue placeholder="Catégorie" /></SelectTrigger>
            <SelectContent><SelectItem value="administration">Administration</SelectItem><SelectItem value="etat_civil">État Civil</SelectItem><SelectItem value="urbanisme">Urbanisme</SelectItem><SelectItem value="social">Social</SelectItem><SelectItem value="technique">Technique</SelectItem></SelectContent>
          </Select>
          <Input placeholder="Horaires" value={formData.hours || ''} onChange={(e) => setFormData({ ...formData, hours: e.target.value })} />
          <Input placeholder="Contact" value={formData.contact || ''} onChange={(e) => setFormData({ ...formData, contact: e.target.value })} />
        </div>);
      case 'documents':
        return (<div className="space-y-4">
          <Input placeholder="Titre du document" value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} />
          <ImageUpload value={formData.file_url || ''} onChange={(url) => setFormData({ ...formData, file_url: url })} placeholder="Télécharger le document (PDF, DOC, images)" />
          <Textarea placeholder="Description (optionnel)" rows={2} value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          <Select value={formData.category || 'etat_civil'} onValueChange={(value) => setFormData({ ...formData, category: value })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="etat_civil">État Civil</SelectItem><SelectItem value="urbanisme">Urbanisme</SelectItem><SelectItem value="fiscalite">Fiscalité</SelectItem><SelectItem value="deliberations">Délibérations</SelectItem></SelectContent>
          </Select>
          {renderPublicationControl()}
        </div>);
      case 'videos':
        return (<div className="space-y-4">
          <Input placeholder="Titre de la vidéo" value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} data-testid="modal-video-title" />
          <Textarea placeholder="Description (optionnel)" rows={2} value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
          <div>
            <label className="text-sm font-medium text-neutral-700 mb-1 block">Vidéo</label>
            <VideoUpload value={formData.video_url || ''} videoType={formData.video_type || null} onChange={(url, type) => setFormData({ ...formData, video_url: url || null, video_type: type || null })} />
          </div>
          <ImageUpload value={formData.thumbnail_url || ''} onChange={(url) => setFormData({ ...formData, thumbnail_url: url })} placeholder="Miniature / vignette (optionnel)" />
          <Select value={formData.category || 'general'} onValueChange={(value) => setFormData({ ...formData, category: value })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="general">Général</SelectItem>
              <SelectItem value="ceremonie">Cérémonie</SelectItem>
              <SelectItem value="conseil">Conseil Municipal</SelectItem>
              <SelectItem value="culture">Culture</SelectItem>
              <SelectItem value="sport">Sport</SelectItem>
              <SelectItem value="education">Éducation</SelectItem>
              <SelectItem value="developpement">Développement</SelectItem>
            </SelectContent>
          </Select>
          {renderPublicationControl()}
        </div>);
      case 'diaspora/projects':
        return (<div className="space-y-4">
          {editingItem && (
            <div className="p-4 bg-neutral-100 rounded-lg">
              <p className="text-sm text-neutral-600 mb-1">Proposé par: <strong>{editingItem.proposer_name}</strong> ({editingItem.proposer_country})</p>
              {editingItem.proposer_email && <p className="text-sm text-neutral-600">Email: {editingItem.proposer_email}</p>}
            </div>
          )}
          <Input placeholder="Titre du projet" value={formData.title || ''} onChange={(e) => setFormData({ ...formData, title: e.target.value })} data-testid="modal-diaspora-title" />
          <Textarea placeholder="Description" rows={3} value={formData.description || ''} onChange={(e) => setFormData({ ...formData, description: e.target.value })} data-testid="modal-diaspora-description" />
          {!editingItem && (
            <>
              <Input placeholder="Nom du proposant" value={formData.proposer_name || ''} onChange={(e) => setFormData({ ...formData, proposer_name: e.target.value })} />
              <Input type="email" placeholder="Email du proposant" value={formData.proposer_email || ''} onChange={(e) => setFormData({ ...formData, proposer_email: e.target.value })} />
              <Input placeholder="Pays du proposant" value={formData.proposer_country || ''} onChange={(e) => setFormData({ ...formData, proposer_country: e.target.value })} />
            </>
          )}
          <Input placeholder="Budget estimé (optionnel)" value={formData.budget_estimate || ''} onChange={(e) => setFormData({ ...formData, budget_estimate: e.target.value })} />
          <Select value={formData.category || 'education'} onValueChange={(value) => setFormData({ ...formData, category: value })}>
            <SelectTrigger><SelectValue placeholder="Catégorie" /></SelectTrigger>
            <SelectContent><SelectItem value="education">Éducation</SelectItem><SelectItem value="sante">Santé</SelectItem><SelectItem value="infrastructure">Infrastructure</SelectItem><SelectItem value="environnement">Environnement</SelectItem></SelectContent>
          </Select>
          <Select value={formData.status || 'en_attente'} onValueChange={(value) => setFormData({ ...formData, status: value })}>
            <SelectTrigger><SelectValue placeholder="Statut" /></SelectTrigger>
            <SelectContent><SelectItem value="en_attente">En attente</SelectItem><SelectItem value="approuve">Approuvé</SelectItem><SelectItem value="en_cours">En cours</SelectItem><SelectItem value="termine">Terminé</SelectItem><SelectItem value="rejete">Rejeté</SelectItem></SelectContent>
          </Select>
        </div>);
      case 'users':
        return (<div className="space-y-4" data-testid="user-modal-form">
          <Input placeholder="Nom complet" value={formData.full_name || ''} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} data-testid="modal-user-fullname" />
          {!editingItem && (
            <>
              <Input type="email" placeholder="Email" value={formData.email || ''} onChange={(e) => setFormData({ ...formData, email: e.target.value })} data-testid="modal-user-email" />
              <Input type="password" placeholder="Mot de passe" value={formData.password || ''} onChange={(e) => setFormData({ ...formData, password: e.target.value })} data-testid="modal-user-password" />
            </>
          )}
          <div>
            <label className="block text-sm font-medium mb-2">Rôle</label>
            <Select value={formData.role || 'contributeur'} onValueChange={(value) => setFormData({ ...formData, role: value })}>
              <SelectTrigger data-testid="modal-user-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="contributeur">Contributeur</SelectItem>
                <SelectItem value="redacteur">Rédacteur</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>);
      default: return null;
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="loader"></div></div>;

  const mainMenuItems = menuItems.filter(i => i.id !== 'settings' && i.id !== 'users');
  const showSettings = hasAccess(role, 'super_admin');
  const showUsers = hasAccess(role, 'super_admin');

  return (
    <div className="min-h-screen bg-neutral-100" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-neutral-900 flex flex-col transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Gandiaye" className="w-10 h-10 rounded-full" />
            <span className="text-white font-heading font-bold">Gandiaye Admin</span>
          </Link>
        </div>
        <nav className="mt-4 flex-1 overflow-y-auto" data-testid="admin-sidebar-nav">
          {mainMenuItems.map((item) => (
            <button key={item.id} onClick={() => { setActiveTab(item.id); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-6 py-3 text-left text-sm transition-colors ${activeTab === item.id ? 'bg-emerald-600 text-white' : 'text-neutral-400 hover:bg-neutral-800 hover:text-white'}`}
              data-testid={`sidebar-${item.id}`}>
              <item.icon size={18} /> {item.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-neutral-700 p-4 space-y-1">
          {showUsers && (
            <button onClick={() => { setActiveTab('users'); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm rounded-lg transition-colors ${activeTab === 'users' ? 'bg-emerald-600 text-white' : 'text-neutral-400 hover:bg-neutral-800 hover:text-white'}`}
              data-testid="sidebar-users">
              <Shield size={18} /> Utilisateurs
            </button>
          )}
          {showSettings && (
            <button onClick={() => { setActiveTab('settings'); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left text-sm rounded-lg transition-colors ${activeTab === 'settings' ? 'bg-emerald-600 text-white' : 'text-neutral-400 hover:bg-neutral-800 hover:text-white'}`}
              data-testid="sidebar-settings">
              <Settings size={18} /> Paramètres
            </button>
          )}
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2.5 text-red-400 hover:bg-red-900/30 rounded-lg transition-colors" data-testid="logout-btn">
            <LogOut size={18} /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        <header className="bg-white shadow-sm sticky top-0 z-40">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(!sidebarOpen)} className="lg:hidden text-neutral-600">
                {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
              <form onSubmit={(e) => { e.preventDefault(); if (adminSearchQuery.trim()) { window.open(`/recherche?q=${encodeURIComponent(adminSearchQuery.trim())}`, '_blank'); } }}
                className="hidden md:flex items-center relative">
                <Search className="absolute left-3 text-neutral-400" size={16} />
                <input value={adminSearchQuery} onChange={(e) => setAdminSearchQuery(e.target.value)}
                  placeholder="Recherche globale..."
                  className="pl-9 pr-3 py-2 text-sm bg-neutral-100 border-0 rounded-lg w-56 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:bg-white"
                  data-testid="admin-search-input" />
              </form>
            </div>
            <div className="flex items-center gap-3">
              {/* Notification Bell */}
              <div className="relative">
                <button onClick={() => setShowNotifPanel(!showNotifPanel)}
                  className="relative p-2 text-neutral-500 hover:text-neutral-700 hover:bg-neutral-100 rounded-lg transition-colors"
                  data-testid="notif-bell-btn">
                  <Bell size={20} />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center" data-testid="notif-unread-count">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                {showNotifPanel && (
                  <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-white rounded-xl shadow-xl border z-50 overflow-hidden" data-testid="notif-panel">
                    <div className="flex items-center justify-between px-4 py-3 bg-neutral-50 border-b">
                      <span className="font-semibold text-sm">Notifications</span>
                      {unreadCount > 0 && (
                        <button onClick={markAllNotifsRead} className="text-xs text-emerald-600 hover:underline flex items-center gap-1" data-testid="mark-all-read-btn">
                          <CheckCheck size={14} /> Tout marquer lu
                        </button>
                      )}
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p className="text-center text-neutral-400 text-sm py-8">Aucune notification</p>
                      ) : (
                        notifications.map(n => (
                          <div key={n.id}
                            className={`px-4 py-3 border-b last:border-0 cursor-pointer hover:bg-neutral-50 transition-colors ${!n.is_read ? 'bg-emerald-50' : ''}`}
                            onClick={() => { if (!n.is_read) markNotifRead(n.id); }}
                            data-testid={`notif-item-${n.id}`}>
                            <p className={`text-sm ${!n.is_read ? 'font-semibold text-neutral-800' : 'text-neutral-600'}`}>{n.message}</p>
                            <p className="text-xs text-neutral-400 mt-1">{new Date(n.created_at).toLocaleString('fr-FR')}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              <span className="text-sm text-neutral-600 hidden sm:inline">Bienvenue, <strong>{user?.full_name}</strong></span>
              <RoleBadge role={role} />
              <Link to="/" className="text-sm text-emerald-600 hover:underline">Voir le site</Link>
            </div>
          </div>
        </header>
        <main className="p-6">{renderContent()}</main>
      </div>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Modifier' : 'Ajouter'} - {modalType === 'users' ? 'Utilisateur' : modalType}</DialogTitle>
            <DialogDescription className="sr-only">Formulaire pour {editingItem ? 'modifier' : 'ajouter'} un élément</DialogDescription>
          </DialogHeader>
          {renderModalContent()}
          <DialogFooter>
            <Button variant="outline" onClick={closeModal}>Annuler</Button>
            <Button onClick={handleSave} className="bg-emerald-600 hover:bg-emerald-700" data-testid="modal-save-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
